import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    RewardsWaysToSpendB2bPageWidgetDefinition,
    RewardsWaysToSpendPageWidgetDefinition,
} from "@apps/rewards/constants/definitions";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import { parseOptionalBooleanDataAttr } from "@ui-utils/Injector/utils";

import RewardsWaysToSpendB2bPageWidget from "./b2b/RewardsWaysToSpendB2bPageWidget";
import RewardsWaysToSpendPageWidget from "./RewardsWaysToSpendPageWidget";

const GW_SHOW_POINTS_BALANCE = "gw-show-points-balance";
const GW_REDIRECT_TO_CART = "gw-redirect-to-cart-after-redeem";

interface RewardsWaysToSpendPageWidgetInjectorConfig
    extends RewardsWidgetInjectorConfig {
    isCustomerB2b: boolean;
}

class RewardsWaysToSpendPageWidgetInjector {
    constructor(
        private readonly config: RewardsWaysToSpendPageWidgetInjectorConfig
    ) {}

    start(): void {
        this.config.logger.debug("RewardsWaysToSpendPageWidgetInjector start", {
            placeholderSelector: this.config.placeholderSelector,
        });

        const targets$ = this.config.mutationObserverInjector.register({
            targetSelector: this.config.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.config.logger?.debug(
                "RewardsWaysToSpendPageWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            let widget:
                | RewardsWaysToSpendPageWidget
                | RewardsWaysToSpendB2bPageWidget;
            let widgetName: REWARDS_WIDGET_NAMES;

            void (async () => {
                if (this.config.isCustomerB2b) {
                    const RewardsWaysToSpendB2bPageWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsWaysToSpendB2bPageWidgetDefinition,
                            true
                        );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_SPEND_B2B_PAGE_WIDGET;
                    widget = new RewardsWaysToSpendB2bPageWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            showPointsBalance: parseOptionalBooleanDataAttr(
                                placeholder,
                                GW_SHOW_POINTS_BALANCE
                            ),
                            redirectToCartAfterRedeem:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    GW_REDIRECT_TO_CART
                                ),
                        },
                        this.config.logger
                    );
                } else {
                    const RewardsWaysToSpendPageWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsWaysToSpendPageWidgetDefinition,
                            true
                        );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_SPEND_PAGE_WIDGET;
                    widget = new RewardsWaysToSpendPageWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            showPointsBalance: parseOptionalBooleanDataAttr(
                                placeholder,
                                GW_SHOW_POINTS_BALANCE
                            ),
                            redirectToCartAfterRedeem:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    GW_REDIRECT_TO_CART
                                ),
                        },
                        this.config.logger
                    );
                }

                await widget.init();

                this.config.eventsCollector.pushEvent(
                    this.config.eventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });
    }
}

export default RewardsWaysToSpendPageWidgetInjector;
